/**
 * src/types/charts/bar-grouped.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Grouped bar chart — side-by-side bars within category groups.
 * Ideal for comparing multiple series across discrete categories:
 *   Q1/Q2/Q3/Q4 × Revenue/Expenses/Profit
 *   Region × Product A/B/C
 *   Before/After × Department 1/2/3/4
 *
 * DATA MODEL:
 *   Each data point is ONE BAR. `category` positions the group on the X axis.
 *   `series` identifies the bar's position within that group.
 *
 *   IMPORTANT: uses `series` (not `seriesName`) for DataGridMatrix compatibility.
 *   DataGridMatrix reads d.category and d.series — same field names as bar-stacked.
 *   This is a deliberate deviation from the multi-series line chart convention.
 *
 * SPACING GEOMETRY:
 *   groupWidth   = barWidth × numSeries + barGap × (numSeries − 1)
 *   totalChartW  = (groupWidth + groupGap) × numCats − groupGap
 *   Groups are centred in the chart area. fitScale is applied if total overflows.
 *   groupGap must always be visually larger than barGap for correct readability.
 *
 * ADDING TO THIS FILE: only change this file and chart.registry.ts.
 *
 * Chart2Video | CORSphere Software Inc.
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_AXES, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';
import { z } from 'zod';


// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * classic      — all bars grow simultaneously with tiny left→right stagger
 * sequential   — group by group, left to right, smooth window
 * staggered    — THE signature style: group arrives, then bars fire in rapid
 *                half-stagger within the group. Between-group gap is 2× within-group.
 * wave         — sine wave propagates left→right across all bars by position
 * growFromAxis — all bars grow simultaneously from the X axis baseline
 * cascade      — series-first: Revenue fires across ALL groups, then Expenses, then Profit
 */
export type BarGroupedAnimationStyle =
  | 'classic' | 'sequential' | 'staggered'
  | 'wave' | 'growFromAxis' | 'cascade';

export const BAR_GROUPED_ANIMATION_STYLES: BarGroupedAnimationStyle[] = [
  'classic', 'sequential', 'staggered', 'wave', 'growFromAxis', 'cascade',
];

/** All styles are valid as per-point element panel overrides. */
export const BAR_GROUPED_ELEMENT_PANEL_STYLES: BarGroupedAnimationStyle[] =
  BAR_GROUPED_ANIMATION_STYLES;


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

export interface BarGroupedDataPoint extends ChartDataPoint {
  chartType:    'bar-grouped';
  /** X-axis group label. e.g. "Q1", "North", "Before". Groups preserved in insertion order. */
  category:     string;
  /**
   * Series this bar belongs to. Determines bar position within the group and
   * legend entry. Uses `series` (not `seriesName`) for DataGridMatrix compatibility.
   * e.g. "Revenue", "Expenses", "Profit".
   */
  series:       string;
  /** Bar height. Negative values render below the zero baseline. */
  value:        number;
  /** Per-bar value label override. When false: auto-hides at narrow widths. */
  showValue?:   boolean;
  labelOffsetX?: number;
  labelOffsetY?: number;
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface BarGroupedSpacingConfig {
  chartType:    'bar-grouped';
  /** Width of each individual bar in px. Range: 8–160. Default: 40. */
  barWidth:     number;
  /** Gap between bars WITHIN a group. Range: 0–40. Default: 6. */
  barGap:       number;
  /** Gap BETWEEN groups. Range: 10–200. Default: 50. Always larger than barGap. */
  groupGap:     number;
  /** Rounded corner radius on bar tips. Range: 0–12. Default: 4. */
  cornerRadius: number;
  /** Show the sum of all series above each group. Default: false. */
  showGroupTotal: boolean;
  /** Per-series color overrides keyed by series name. */
  seriesOverrides?: Record<string, { color?: string }>;
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_GROUPED_DEFAULTS: ChartConfigBase<BarGroupedSpacingConfig> = {
  chartType: 'bar-grouped',
  content: {
    titles:    [makeTitle('Performance Comparison')],
    subtitles: [makeSubtitle('Revenue · Expenses · Profit by Quarter')],
    infoBoxes: [],
  },
  animation: { duration: 5, style: 'staggered', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    chartMargin:   { top: 180, right: 80, bottom: 130, left: 100 },
  },
  axes:       { ...DEFAULT_AXES },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:      'bar-grouped',
    barWidth:       40,
    barGap:         6,
    groupGap:       50,
    cornerRadius:   4,
    showGroupTotal: false,
    seriesOverrides: {},
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};


// ─────────────────────────────────────────────────────────────────────────────
// ZOD SPACING SCHEMA
// ─────────────────────────────────────────────────────────────────────────────

export const barGroupedSpacingZodSchema = z.object({
  chartType:      z.literal('bar-grouped'),
  barWidth:       z.number().min(8).max(160),
  barGap:         z.number().min(0).max(40),
  groupGap:       z.number().min(10).max(200),
  cornerRadius:   z.number().min(0).max(12),
  showGroupTotal: z.boolean(),
  seriesOverrides: z.record(z.string(), z.object({
    color: z.string().optional(),
  })).optional(),
});


// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_GROUPED_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'bar-grouped.barWidth': {
    description: 'Width of each individual bar in canvas pixels.',
    type:    'number',
    range:   '8–160',
    default: 40,
    unit:    'canvas pixels',
    affects: ['per-bar visual width', 'total group width'],
    bounds:  ['Min 8. Max 160.'],
    synergizes: ['bar-grouped.barGap — together control within-group density'],
    tensions:   ['series count — with 4+ series, barWidth 40 × 4 + barGap × 3 = 178px per group, may overflow'],
    agentNote:  "groupWidth = barWidth × numSeries + barGap × (numSeries−1). At 1920px canvas with default margins, 4 groups × 3 series fits comfortably at barWidth=40. For 4 series reduce to 28-32. For 5+ series reduce to 20-28.",
  },

  'bar-grouped.barGap': {
    description: 'Gap between bars WITHIN a group in canvas pixels. Always smaller than groupGap for readable visual separation.',
    type:    'number',
    range:   '0–40',
    default: 6,
    unit:    'canvas pixels',
    affects: ['within-group spacing', 'group width'],
    bounds:  ['Min 0. Max 40. Should be < groupGap.'],
    synergizes: ['bar-grouped.groupGap — the visual ratio barGap:groupGap communicates group vs series hierarchy'],
    tensions:   [],
    agentNote:  "Keep barGap < groupGap at all times. Recommended ratio: barGap ≈ groupGap / 8. Default 6/50 = 12% is well-calibrated. If bars look crowded within groups, increase to 8-10. Never exceed groupGap.",
  },

  'bar-grouped.groupGap': {
    description: 'Gap BETWEEN category groups in canvas pixels. Must be visually larger than barGap to communicate the group hierarchy.',
    type:    'number',
    range:   '10–200',
    default: 50,
    unit:    'canvas pixels',
    affects: ['between-group separation', 'total chart width'],
    bounds:  ['Min 10. Max 200. Must be > barGap for readable grouping.'],
    synergizes: ['bar-grouped.barGap — the contrast between these two gaps is what makes groups readable'],
    tensions:   ['category count — large groupGap with many categories will overflow chart width'],
    agentNote:  "groupGap creates the visual 'category separation'. With 4+ groups reduce to 30-40. With 2-3 groups can increase to 80-120 for dramatic category separation. groupGap < barGap will make bars look like they belong to different groups than they do — always keep groupGap > barGap.",
  },

  'bar-grouped.cornerRadius': {
    description: 'Rounded corner radius in px applied to the bar tips. Positive bars: rounded top. Negative bars: rounded bottom.',
    type:    'number',
    range:   '0–12',
    default: 4,
    unit:    'canvas pixels',
    affects: ['bar tip visual style'],
    bounds:  ['Min 0 (sharp). Max 12.'],
    synergizes: [],
    tensions:   ['barWidth — at barWidth < 16px, cornerRadius > 4 makes bars look like circles. Clamp: Math.min(cornerRadius, barWidth * 0.35).'],
    agentNote:  "0 = sharp industrial look. 4 = default modern data viz (matches Material, Recharts defaults). 8-12 = pill-like, better for presentations than analysis.",
  },

  'bar-grouped.showGroupTotal': {
    description: 'When true, draws the sum of all series for each group above the tallest bar in that group.',
    type:    'boolean',
    range:   'true | false',
    default: false,
    unit:    'boolean',
    affects: ['group total label above each group'],
    bounds:  ['Only drawn when all series values in the group are non-negative.'],
    synergizes: [],
    tensions:   ['negative values — suppressed automatically when any series is negative in that group'],
    agentNote:  "Adds editorial context: 'Q4 total was 2,760'. Useful when the story is both individual series AND the aggregate. Disable if Y axis already communicates totals clearly.",
  },

  'dataPoint.bar-grouped.category': {
    description: 'Category identifier — determines which group this bar belongs to. All bars with the same category form one side-by-side group.',
    type:    'string',
    range:   'any string',
    default: null,
    unit:    'string',
    affects: ['group position on X axis', 'X axis category label'],
    bounds:  ['Groups appear in insertion order of first occurrence.'],
    synergizes: ['dataPoint.bar-grouped.series — together form the category×series grid'],
    tensions:   [],
    agentNote:  "Category order = insertion order of first occurrence. Data does NOT need to be sorted. Recommendation: 2–6 categories. Beyond 8, switch to a horizontal bar chart for readability.",
  },

  'dataPoint.bar-grouped.series': {
    description: 'Series identifier — determines bar position within the group and legend entry. Uses `series` field (not `seriesName`) for DataGridMatrix compatibility.',
    type:    'string',
    range:   'any string',
    default: null,
    unit:    'string',
    affects: ['bar position within group', 'legend entry', 'bar color'],
    bounds:  ['Series appear in insertion order of first occurrence.'],
    synergizes: ['dataPoint.bar-grouped.category'],
    tensions:   [],
    agentNote:  "Series order = insertion order. The first series encountered becomes the leftmost bar in every group. Max 6 series before colour clash and grouping becomes unreadable — validator warns at 7+. For 7+ series consider heatmap instead.",
  },

  'dataPoint.bar-grouped.value': {
    description: 'Bar height. Negative values are supported and render below the zero baseline.',
    type:    'number',
    range:   'any number; zero is valid',
    default: null,
    unit:    'arbitrary numeric unit (user-defined)',
    affects: ['bar height', 'Y axis range', 'value label position'],
    bounds:  ['Negative values supported. Zero renders as an empty stub bar.'],
    synergizes: ['dataLabels — value labels auto-hide at narrow barWidths when showValue is not forced'],
    tensions:   ['within-group scale — if one series is 10× another, the smaller series bars become near-invisible slivers'],
    agentNote:  "Negative values flip the bar below the zero baseline. A dashed zero-crossing line is drawn automatically. If mixing positive and negative values in the same chart, ensure Y axis minValue is 'auto' not 'zero'.",
  },

};